/**
 * CR319: Promo Badges Accordion for Deal Cards → Structure tab
 * 
 * Collapsed accordion showing:
 * - Master enable toggle
 * - Drag-sortable list of badge types with enabled toggles
 * - Max badges control
 * 
 * Persists to template_json.promo_badges and updates preview immediately.
 */

import { useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  ChevronDown,
  ChevronRight,
  GripVertical,
  BadgeCheck,
  Lock,
  Building2,
  HelpCircle,
  Package,
  Info,
  AlertCircle,
  RotateCcw,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { PromoBadgeKind } from "@/lib/deal-cards/helpers/getPromoBadges";

// ================================================================
// TYPES
// ================================================================

export interface PromoBadgesConfig {
  enabled: boolean;
  order: PromoBadgeKind[];
  badges: Record<PromoBadgeKind, { enabled: boolean }>;
}

export const DEFAULT_PROMO_BADGES_CONFIG: PromoBadgesConfig = {
  enabled: true,
  order: ["membership", "corporate", "unclassified", "bulk", "details_vary", "confirm_on_site"],
  badges: {
    membership: { enabled: true },
    corporate: { enabled: true },
    unclassified: { enabled: true },
    bulk: { enabled: true },
    details_vary: { enabled: true },
    confirm_on_site: { enabled: true },
  },
};

/**
 * CR327 hotfix: Normalize a potentially null/partial promo badges config onto defaults.
 */
export function normalizePromoBadgesConfig(
  raw?: PromoBadgesConfig | null | Partial<PromoBadgesConfig>
): PromoBadgesConfig {
  if (!raw || typeof raw !== "object") return { ...DEFAULT_PROMO_BADGES_CONFIG };

  const defaults = DEFAULT_PROMO_BADGES_CONFIG;

  const mergedBadges = { ...defaults.badges };
  if (raw.badges && typeof raw.badges === "object") {
    for (const key of Object.keys(mergedBadges) as PromoBadgeKind[]) {
      if (raw.badges[key]) {
        mergedBadges[key] = { ...mergedBadges[key], ...raw.badges[key] };
      }
    }
  }

  let mergedOrder = defaults.order;
  if (Array.isArray(raw.order) && raw.order.length > 0) {
    const validKeys = new Set<string>(Object.keys(defaults.badges));
    const filtered = raw.order.filter((k): k is PromoBadgeKind => validKeys.has(k));
    if (filtered.length > 0) {
      const missing = defaults.order.filter(k => !filtered.includes(k));
      mergedOrder = [...filtered, ...missing];
    }
  }

  return {
    enabled: typeof raw.enabled === "boolean" ? raw.enabled : defaults.enabled,
    order: mergedOrder,
    badges: mergedBadges,
  };
}

interface PromoBadgesAccordionProps {
  config?: PromoBadgesConfig | null;
  onChange: (config: PromoBadgesConfig) => void;
  defaultOpen?: boolean;
  /** CR340: When true, content is always visible (no accordion expand/collapse) */
  alwaysExpanded?: boolean;
  className?: string;
}

// Badge metadata
const BADGE_META: Record<PromoBadgeKind, { label: string; icon: React.ElementType; description: string }> = {
  membership: { label: "Membership", icon: Lock, description: "Members-only deals" },
  corporate: { label: "Corporate", icon: Building2, description: "Corporate/bulk deals" },
  unclassified: { label: "Unclassified", icon: HelpCircle, description: "Unclassified deals" },
  bulk: { label: "Bulk Deal", icon: Package, description: "Bulk purchase deals" },
  details_vary: { label: "Details Vary", icon: Info, description: "Incomplete details" },
  confirm_on_site: { label: "Confirm on Site", icon: AlertCircle, description: "Verify on merchant site" },
};

// ================================================================
// COMPONENT
// ================================================================

export function PromoBadgesAccordion({
  config: rawConfig,
  onChange,
  defaultOpen = false,
  alwaysExpanded = false,
  className,
}: PromoBadgesAccordionProps) {
  // CR327 hotfix: always normalize to prevent crashes from null/partial config
  const config = useMemo(() => normalizePromoBadgesConfig(rawConfig), [rawConfig]);
  const [isOpen, setIsOpen] = useState(defaultOpen || alwaysExpanded);
  const [activeId, setActiveId] = useState<string | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Handlers
  const handleMasterToggle = useCallback((enabled: boolean) => {
    onChange({ ...config, enabled });
  }, [config, onChange]);

  const handleBadgeToggle = useCallback((key: PromoBadgeKind, enabled: boolean) => {
    onChange({
      ...config,
      badges: {
        ...config.badges,
        [key]: { ...config.badges[key], enabled },
      },
    });
  }, [config, onChange]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = config.order.indexOf(active.id as PromoBadgeKind);
    const newIndex = config.order.indexOf(over.id as PromoBadgeKind);
    if (oldIndex === -1 || newIndex === -1) return;

    onChange({
      ...config,
      order: arrayMove(config.order, oldIndex, newIndex),
    });
  }, [config, onChange]);

  const handleReset = useCallback(() => {
    onChange(DEFAULT_PROMO_BADGES_CONFIG);
  }, [onChange]);

  // Count enabled badges
  const enabledCount = useMemo(() => {
    return Object.values(config.badges).filter(b => b.enabled).length;
  }, [config.badges]);

  // Check if order differs from default
  const hasCustomOrder = useMemo(() => {
    return config.order.some((k, i) => k !== DEFAULT_PROMO_BADGES_CONFIG.order[i]);
  }, [config.order]);

  return (
    <Collapsible open={alwaysExpanded || isOpen} onOpenChange={alwaysExpanded ? undefined : setIsOpen} className={className}>
      {alwaysExpanded ? (
        <div className="flex items-center justify-between w-full p-3 rounded-lg border bg-muted/30">
          <div className="flex items-center gap-2">
            <BadgeCheck className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Promo Badges</span>
            <span className="text-xs text-muted-foreground">
              ({enabledCount}/{Object.keys(config.badges).length})
            </span>
          </div>
          <Badge variant={config.enabled ? "secondary" : "outline"} className="text-[10px]">
            {config.enabled ? "On" : "Off"}
          </Badge>
        </div>
      ) : (
        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors">
          <div className="flex items-center gap-2">
            {isOpen ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
            <BadgeCheck className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Promo Badges</span>
            <span className="text-xs text-muted-foreground">
              ({enabledCount}/{Object.keys(config.badges).length})
            </span>
          </div>
          <Badge variant={config.enabled ? "secondary" : "outline"} className="text-[10px]">
            {config.enabled ? "On" : "Off"}
          </Badge>
        </CollapsibleTrigger>
      )}

      <CollapsibleContent className="pt-2 space-y-3">
        {/* Master toggle */}
        <div className="flex items-center justify-between px-3 py-2 border rounded-lg bg-card">
          <div>
            <Label className="text-sm font-normal">Enable Promo Badges</Label>
            <p className="text-xs text-muted-foreground">Show badges on deal cards</p>
          </div>
          <Switch
            checked={config.enabled}
            onCheckedChange={handleMasterToggle}
          />
        </div>

        {config.enabled && (
          <>
            {/* Order header */}
            <div className="flex items-center justify-between px-1">
              <Label className="text-xs text-muted-foreground">Display Order</Label>
              {hasCustomOrder && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleReset}
                  className="h-6 text-[10px] gap-1 px-2"
                >
                  <RotateCcw className="h-3 w-3" />
                  Reset
                </Button>
              )}
            </div>

            {/* Sortable badge list */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={({ active }) => setActiveId(active.id as string)}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={config.order} strategy={verticalListSortingStrategy}>
                <div className="space-y-1">
                  {config.order.map((key) => (
                    <SortableBadgeRow
                      key={key}
                      id={key}
                      meta={BADGE_META[key]}
                      enabled={config.badges[key]?.enabled ?? true}
                      onToggle={(enabled) => handleBadgeToggle(key, enabled)}
                    />
                  ))}
                </div>
              </SortableContext>

              <DragOverlay>
                {activeId && BADGE_META[activeId as PromoBadgeKind] && (
                  <div className="flex items-center gap-2 py-1.5 px-2 rounded-md border bg-background shadow-lg">
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{BADGE_META[activeId as PromoBadgeKind].label}</span>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          </>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

// ================================================================
// SORTABLE ROW
// ================================================================

interface SortableBadgeRowProps {
  id: string;
  meta: { label: string; icon: React.ElementType; description: string };
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
}

function SortableBadgeRow({ id, meta, enabled, onToggle }: SortableBadgeRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const Icon = meta.icon;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-2 py-1.5 px-2 rounded-md border bg-background transition-colors",
        isDragging && "opacity-50 border-primary/50 shadow-md",
        !enabled && "opacity-60"
      )}
    >
      {/* Drag Handle */}
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing touch-none flex-shrink-0"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
      </button>

      {/* Icon */}
      <Icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />

      {/* Label */}
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="flex-1 text-sm cursor-default truncate">{meta.label}</span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">{meta.description}</p>
        </TooltipContent>
      </Tooltip>

      {/* Toggle */}
      <Switch
        checked={enabled}
        onCheckedChange={onToggle}
        className="flex-shrink-0"
      />
    </div>
  );
}

export default PromoBadgesAccordion;
